import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Sparkles, RefreshCw, History, ArrowRight, ThumbsUp, ThumbsDown, Loader2, EyeOff } from "lucide-react";
import { Markdown } from "@/lib/markdown";
import { toast } from "sonner";

// L1 — summary layer: weight badge + property chip + ≤16 字 headline + one grey line; details in a side sheet
export const PRIORITY_META = {
  primary:   { label: "主要 70%", accent: "border-rose-200",    badgeCls: "bg-rose-100 text-rose-800" },
  secondary: { label: "次要 20%", accent: "border-amber-200",   badgeCls: "bg-amber-100 text-amber-800" },
  tertiary:  { label: "參考 10%", accent: "border-emerald-200", badgeCls: "bg-emerald-100 text-emerald-800" },
};
const mdhm = (iso) => (iso ? new Date(iso).toLocaleString("zh-TW", { timeZone: "Asia/Taipei", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).replace(/\//g, "/") : "");

function targetLink(rec) {
  if (rec.target_property_id) return `/funnel?property=${rec.target_property_id}`;
  if (rec.target_stage) return `/funnel?preset=diagnose_weak_stage&metric=${rec.target_stage}`;
  return "/advisor";
}

function RecCard({ rec, onOpen, suppress }) {
  const meta = PRIORITY_META[rec.priority] || PRIORITY_META.tertiary;
  const t = `daily-rec-${rec.priority}`;
  const [hover, setHover] = useState(false);
  return (
    <HoverCard openDelay={150} closeDelay={80} open={hover && !suppress} onOpenChange={setHover}>
      <HoverCardTrigger asChild>
        <button type="button" onClick={() => { setHover(false); onOpen(); }} data-testid={t}
          className={`text-left h-full w-full flex flex-col rounded-xl border-2 ${meta.accent} bg-white px-4 py-3 hover:shadow-md transition-shadow`}>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.badgeCls}`} data-testid={`${t}-priority`}>{meta.label}</span>
            <span className="truncate rounded-full border border-slate-200 bg-slate-50 px-2 text-[10px] text-slate-600" data-testid={`${t}-property`}>{rec.property_name || "全場域"}</span>
          </div>
          <div className="mt-2 text-lg font-bold leading-snug text-slate-900" data-testid={`${t}-title`}>{rec.headline}</div>
          {rec.one_liner && <div className="mt-auto pt-1 text-xs text-slate-500 truncate" data-testid={`${t}-oneliner`}>{rec.one_liner}</div>}
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="w-[300px] bg-white border border-gray-200 text-xs text-slate-700 p-3 shadow-xl" data-testid={`${t}-preview`}>
        <div className="line-clamp-3">{rec.body || rec.one_liner}</div>
        <div className="mt-1.5 text-[10px] text-slate-400">點卡片看完整說明</div>
      </HoverCardContent>
    </HoverCard>
  );
}

function RecDetail({ brief, rec, idx, onClose, onFeedback, onDismiss }) {
  const meta = rec ? PRIORITY_META[rec.priority] || PRIORITY_META.tertiary : null;
  return (
    <Sheet open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="daily-rec-detail">
        {rec && <>
          <SheetHeader className="text-left">
            <div className="flex items-center gap-1.5">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.badgeCls}`}>{meta.label}</span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 text-[10px] text-slate-600">{rec.property_name || "全場域"}</span>
              {rec.time_horizon && <span className="text-[10px] text-slate-500">{rec.time_horizon}</span>}
            </div>
            <SheetTitle className="text-xl" data-testid="daily-rec-detail-title">{rec.headline}</SheetTitle>
            <SheetDescription>{rec.title}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <Markdown content={rec.details} />
            {rec.evidence?.length > 0 && (
              <div data-testid="daily-rec-detail-evidence"><div className="text-sm font-semibold text-slate-800 mb-1">數據依據</div>
                <ul className="list-disc ml-5 text-sm text-slate-700 space-y-0.5">{rec.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul></div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={targetLink(rec)}><Button size="sm" className="bg-[#F26A1F] hover:bg-[#D55B15] text-white h-8 text-xs" data-testid="daily-rec-detail-action">立即行動 <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
              <Button size="sm" variant="outline" className="h-8" onClick={() => onFeedback(idx, "up")} data-testid="daily-rec-detail-up"><ThumbsUp className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => onFeedback(idx, "down")} data-testid="daily-rec-detail-down"><ThumbsDown className="h-3.5 w-3.5" /></Button>
              {onDismiss && <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500" onClick={onDismiss} data-testid="daily-rec-detail-dismiss"><EyeOff className="h-3.5 w-3.5 mr-1" />隱藏今日建議</Button>}
            </div>
            <div className="border-t border-slate-100 pt-2 text-[11px] text-slate-400 font-mono" data-testid="daily-brief-usage">
              {brief.model || "claude-opus-5-5"} · 分析 {brief.time_range?.since} ~ {brief.time_range?.until} · {brief.tokens?.total?.toLocaleString() || 0} tokens · 約 NT$ {brief.cost?.twd ?? 0}
            </div>
          </div>
        </>}
      </SheetContent>
    </Sheet>
  );
}

export function BriefCards({ brief, onDismiss, testid = "daily-brief-cards" }) {
  const [open, setOpen] = useState(null);
  const recs = brief?.recommendations || [];
  const sendFeedback = async (idx, rating) => {
    try { await api.post(`/advisor/daily-brief/${brief.id}/feedback`, { recommendation_index: idx, rating }); toast.success(rating === "up" ? "感謝回饋" : "已記錄，我們會改善"); }
    catch (e) { /* silent */ }
  };
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={testid}>
        {recs.slice(0, 3).map((r, i) => <RecCard key={i} rec={r} suppress={open != null} onOpen={() => setOpen(i)} />)}
      </div>
      <RecDetail brief={brief} rec={open == null ? null : recs[open]} idx={open} onClose={() => setOpen(null)} onFeedback={sendFeedback}
        onDismiss={onDismiss && (() => { setOpen(null); onDismiss(); })} />
    </>
  );
}

export function BriefHistoryList({ history }) {
  if (!history.length) return <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-4 text-center">尚無歷史紀錄</div>;
  return (
    <div className="space-y-2">
      {history.map((b) => (
        <details key={b.id} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs" data-testid={`daily-brief-history-${b.id}`}>
          <summary className="cursor-pointer flex items-center gap-2 text-slate-700">
            <span className="font-mono text-slate-500 shrink-0">{mdhm(b.generated_at)}</span>
            <span className="truncate">{(b.recommendations || []).map((r) => r.headline).join(" · ")}</span>
          </summary>
          <div className="mt-2"><BriefCards brief={b} testid={`daily-brief-history-cards-${b.id}`} /></div>
        </details>
      ))}
    </div>
  );
}

export default function DailyBriefSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  useEffect(() => {
    api.get("/advisor/daily-brief/today").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  const regenerate = async () => {
    setRegenerating(true);
    try { const r = await api.post("/advisor/daily-brief/regenerate"); setData({ brief: r.data.brief, status: "ready" }); toast.success("已重新產生今日建議"); }
    catch (e) { toast.error(e?.response?.data?.detail || "重新產生失敗"); }
    finally { setRegenerating(false); }
  };
  const dismiss = async () => {
    try { await api.post(`/advisor/daily-brief/${data.brief.id}/dismiss`); setData((d) => ({ ...d, brief: { ...d.brief, user_dismissed: true } })); } catch (e) { /* silent */ }
  };
  if (loading) return <Skeleton className="h-28 w-full rounded-2xl" data-testid="daily-brief-loading" />;
  const brief = data?.brief;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3" data-testid="daily-brief-section">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-[#F26A1F] shrink-0" />
          <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">包棟大師今日建議</span>
          {brief && <span className="text-xs text-slate-500 font-mono" data-testid="daily-brief-time">· {mdhm(brief.generated_at)}</span>}
        </div>
        <div className="inline-flex items-center gap-1">
          <Link to="/advisor?tab=daily-brief" title="查看歷史" className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="daily-brief-history-link"><History className="h-3.5 w-3.5" /></Link>
          <button type="button" onClick={regenerate} disabled={regenerating} title="重新產生" className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50" data-testid="daily-brief-regenerate">
            {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {!brief ? (
        <Card className="bg-slate-50 border-dashed border-slate-300" data-testid="daily-brief-pending">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <span className="text-sm text-slate-500">{data?.message || "包棟大師正在分析中… 預計 09:00 送達"}</span>
            <Button size="sm" onClick={regenerate} disabled={regenerating} className="bg-[#F26A1F] hover:bg-[#D55B15] text-white h-8 text-xs" data-testid="daily-brief-manual-generate">
              {regenerating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}手動產生
            </Button>
          </CardContent>
        </Card>
      ) : brief.user_dismissed ? (
        <div className="text-xs text-slate-400" data-testid="daily-brief-dismissed">今日建議已隱藏 · <Link to="/advisor?tab=daily-brief" className="underline">查看</Link></div>
      ) : <BriefCards brief={brief} onDismiss={dismiss} />}
    </div>
  );
}
